import { FC, useCallback, useMemo, useState } from 'react'
import { DragDropContext, DropResult } from 'react-beautiful-dnd'
import { useFormContext } from 'react-hook-form'
import { DEFAULT_ROW_ELEMENT } from '@mobileConstants'
import { FIELD_VALUE_TYPE } from '@constants'
import {
  createEmptyRow,
  createRowElementFromField,
  moveElementBetweenRows,
  removeRow,
  reorderRowElements,
  reorderRows,
} from '@mobileHelpers'
import { MobileListItem, RowElement } from '@mobileTypes'
import { Box, Divider } from '@mui/material'

import { useFetchMultipleObjectsByCodeQuery } from '@redux/api/objects.api'

import { ObjectFieldDTO } from '@types'

import { AvailableFieldsPanel } from './AvailableFieldsPanel'
import { ConfiguredRowsPanel } from './ConfiguredRowsPanel'
import { FieldConfig, FieldConfigDialog } from './FieldConfigDialog'

export const RowsConfigTab: FC = () => {
  const { watch, setValue } = useFormContext<MobileListItem>()

  const getDataCommands = watch('getDataCommands')
  const rows = watch('rows') || []
  const dataSource = watch('dataSource')
  const variables = watch('variables') || []
  const prefilledVariables = watch('prefilledVariables') || []

  // State для конфигурации поля (создание или редактирование)
  const [fieldConfigState, setFieldConfigState] = useState<{
    mode: 'create' | 'edit'
    destination?: { droppableId: string; index: number }
    rowIndex?: number
    elementIndex?: number
    initialData?: Partial<any>
  } | null>(null)

  // Создаем информацию о командах
  const baseCommandsInfo = useMemo(() => {
    if (!getDataCommands?.length) return []

    return getDataCommands
      .map((cmd, index) => ({
        index,
        commandKey: `command${index + 1}`,
        commandName: cmd.name || `command${index + 1}`,
        objectCode: cmd.objectCode,
        objectTitle: cmd.objectTitle || cmd.objectCode,
      }))
      .filter(info => info.objectCode)
  }, [getDataCommands])

  // Добавляем prefilled variable к commandsInfo если выбран
  const commandsInfo = useMemo(() => {
    const commands = [...baseCommandsInfo]
    
    // Если DataSource - prefilled variable, добавляем его как "команду"
    if (dataSource?.bindType === 'prefilled' && dataSource.prefilledVariable) {
      const prefilledVar = prefilledVariables.find(pv => pv.internalId === dataSource.prefilledVariable)
      if (prefilledVar) {
        const localVar = variables.find(v => v.internalId === prefilledVar.internalId)
        if (localVar?.objectCode) {
          if (!commands.find(c => c.commandKey === 'prefilled')) {
            commands.push({
              index: -1,
              commandKey: 'prefilled',
              commandName: localVar.internalId,
              objectCode: localVar.objectCode,
              objectTitle: localVar.objectCode,
            })
          }
        }
      }
    }
    
    return commands
  }, [baseCommandsInfo, dataSource, prefilledVariables, variables])

  // Получаем objectCode из prefilled variable (если выбран)
  const prefilledVariableObjectCode = useMemo(() => {
    if (dataSource?.bindType === 'prefilled' && dataSource.prefilledVariable) {
      const prefilledVar = prefilledVariables.find(pv => pv.internalId === dataSource.prefilledVariable)
      if (prefilledVar) {
        const localVar = variables.find(v => v.internalId === prefilledVar.internalId)
        return localVar?.objectCode
      }
    }
    return undefined
  }, [dataSource, prefilledVariables, variables])

  // Собираем все objectCodes (из команд + из prefilled variable)
  const objectCodes = useMemo(() => {
    const codes = commandsInfo.map(info => info.objectCode)
    if (prefilledVariableObjectCode && !codes.includes(prefilledVariableObjectCode)) {
      codes.push(prefilledVariableObjectCode)
    }
    return codes
  }, [commandsInfo, prefilledVariableObjectCode])

  // Загружаем объекты
  const { data: objectModels = [] } = useFetchMultipleObjectsByCodeQuery(objectCodes, {
    skip: objectCodes.length === 0,
  })

  // Формируем список доступных полей
  const availableFields = useMemo(() => {
    const fields: Array<
      ObjectFieldDTO & { commandKey: string; commandName: string; objectCode: string }
    > = []

    // Если DataSource - prefilled variable, показываем поля объекта переменной
    if (dataSource?.bindType === 'prefilled' && prefilledVariableObjectCode) {
      const objectModel = objectModels.find(model => model.code === prefilledVariableObjectCode)
      
      if (objectModel?.fields) {
        objectModel.fields.forEach(field => {
          fields.push({
            ...field,
            commandKey: 'prefilled',
            commandName: dataSource.prefilledVariable || 'Prefilled Variable',
            objectCode: prefilledVariableObjectCode,
          })
        })
      }
      
      return fields
    }

    // Если DataSource - конкретная команда, показываем только её поля
    const commandsToShow =
      dataSource?.bindType === 'command' && dataSource.commandKey
        ? commandsInfo.filter(c => c.commandKey === dataSource.commandKey)
        : commandsInfo

    commandsToShow.forEach(commandInfo => {
      const objectModel = objectModels.find(model => model.code === commandInfo.objectCode)

      if (objectModel?.fields) {
        objectModel.fields.forEach(field => {
          fields.push({
            ...field,
            commandKey: commandInfo.commandKey,
            commandName: commandInfo.commandName,
            objectCode: commandInfo.objectCode,
          })
        })
      }
    })

    return fields
  }, [commandsInfo, objectModels, dataSource, prefilledVariableObjectCode])

  const handleAddRow = useCallback(() => {
    const newRow = createEmptyRow()
    setValue('rows', [...rows, newRow], { shouldDirty: true })
  }, [rows, setValue])

  const handleDeleteRow = useCallback(
    (rowIndex: number) => {
      const updatedRows = removeRow(rows, rowIndex)
      setValue('rows', updatedRows, { shouldDirty: true })
    },
    [rows, setValue]
  )

  const handleDeleteElement = useCallback(
    (rowIndex: number, elementIndex: number) => {
      const updatedRows = [...rows]
      const row = { ...updatedRows[rowIndex] }

      // Удаляем элемент
      row.elements = row.elements.filter((_, idx) => idx !== elementIndex)
      // Обновляем order
      row.elements = row.elements.map((el, idx) => ({ ...el, order: idx }))

      updatedRows[rowIndex] = row
      setValue('rows', updatedRows, { shouldDirty: true })
    },
    [rows, setValue]
  )

  const handleSaveFieldConfig = useCallback(
    (config: FieldConfig) => {
      if (!fieldConfigState) return

      const updatedRows = [...rows]

      if (fieldConfigState.mode === 'create') {
        // Создание нового элемента
        const destRowIndex = parseInt(fieldConfigState.destination!.droppableId.split(':')[1])
        const row = { ...updatedRows[destRowIndex] }

        let newElement: RowElement

        if (config.bindType === 'field') {
          // Найти поле по commandKey и fieldName
          const field = availableFields.find(
            f => f.commandKey === config.commandKey && f.name === config.fieldName
          )
          if (!field) return

          newElement = createRowElementFromField(
            field,
            config.commandKey!,
            fieldConfigState.destination!.index
          )
        } else {
          // JS поле
          newElement = {
            ...DEFAULT_ROW_ELEMENT,
            code: `custom_${Date.now()}`,
            order: fieldConfigState.destination!.index,
            title: config.title,
            value: config.jsCode || '',
            objectCode: '',
            valueType: null,
            fieldType: null,
            bindType: 'js',
          } as RowElement
        }

        // Применяем общие настройки
        newElement.title = config.title
        newElement.useDisplayRule = config.useDisplayRule
        newElement.formatDate = config.formatDate || null
        newElement.pathArray = config.bindType === 'field' ? (config.pathArray || null) : null
        if (config.displayRule) {
          newElement.displayRule = {
            id: config.displayRule.id || 0,
            code: (config.displayRule.code || '') as string,
            title: (config.displayRule.title || '') as string,
            objectCode: (config.displayRule.objectCode || '') as string,
            objectTitle: (config.displayRule.objectTitle || '') as string,
            objectField: (config.displayRule.objectField || undefined) as string | undefined,
            isJs: false,
            jsSetting: undefined as any,
            displayRuleValues: null as any,
          } as any
        } else {
          newElement.displayRule = null
        }

        row.elements.splice(fieldConfigState.destination!.index, 0, newElement)
        row.elements = row.elements.map((el, idx) => ({ ...el, order: idx }))
        updatedRows[destRowIndex] = row
      } else {
        // Редактирование существующего элемента
        const row = { ...updatedRows[fieldConfigState.rowIndex!] }
        const element = { ...row.elements[fieldConfigState.elementIndex!] }

        element.title = config.title
        element.useDisplayRule = config.useDisplayRule

        if (config.bindType === 'field') {
          const field = availableFields.find(
            f => f.commandKey === config.commandKey && f.name === config.fieldName
          )
          if (field) {
            element.code = field.name
            element.value = field.name
            element.objectCode = field.objectCode
            element.valueType = field.valueType
            element.bindType = 'field'
            element.formatDate = config.formatDate || null
            element.pathArray = config.pathArray || null
          }
        } else {
          element.value = config.jsCode || ''
          element.bindType = 'js'
          element.formatDate = null
          element.pathArray = null
        }

        if (config.displayRule) {
          element.displayRule = {
            id: config.displayRule.id || 0,
            code: (config.displayRule.code || '') as string,
            title: (config.displayRule.title || '') as string,
            objectCode: (config.displayRule.objectCode || '') as string,
            objectTitle: (config.displayRule.objectTitle || '') as string,
            objectField: (config.displayRule.objectField || undefined) as string | undefined,
            isJs: false,
            jsSetting: undefined as any,
            displayRuleValues: null as any,
          } as any
        } else {
          element.displayRule = null
        }

        row.elements[fieldConfigState.elementIndex!] = element
        updatedRows[fieldConfigState.rowIndex!] = row
      }

      setValue('rows', updatedRows, { shouldDirty: true })
      setFieldConfigState(null)
    },
    [fieldConfigState, rows, availableFields, setValue]
  )

  const handleEditElement = useCallback(
    (rowIndex: number, elementIndex: number) => {
      const element = rows[rowIndex]?.elements[elementIndex]
      if (!element) return

      // Подготовить initialData для редактирования
      const initialData: any = {
        title: element.title || '',
        bindType: element.bindType,
        useDisplayRule: element.useDisplayRule,
        displayRule: element.displayRule
          ? {
              id: element.displayRule.id,
              code: element.displayRule.code,
              title: element.displayRule.title,
              objectCode: element.displayRule.objectCode,
              objectTitle: element.displayRule.objectTitle,
              objectField: (element.displayRule as any).objectField || undefined,
            }
          : null,
        formatDate: element.formatDate || null,
      }

      if (element.bindType === 'field') {
        // Найти команду по objectCode
        const fieldName = element.code
        const command = commandsInfo.find(c => c.objectCode === element.objectCode)

        if (command) {
          initialData.command = {
            key: command.commandKey,
            name: command.commandName,
            objectCode: command.objectCode,
            objectTitle: command.objectTitle,
          }

          // Найти поле в availableFields
          const field = availableFields.find(
            f => f.commandKey === command.commandKey && f.name === fieldName
          )
          if (field) {
            initialData.field = field
          }
          
          // Добавляем pathArray если есть
          initialData.pathArray = element.pathArray || null
        }
      } else {
        initialData.jsCode = element.value || ''
      }

      setFieldConfigState({
        mode: 'edit',
        rowIndex,
        elementIndex,
        initialData,
      })
    },
    [rows, commandsInfo, availableFields]
  )

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination, type } = result

      if (!destination) return

      if (destination.index === source.index && destination.droppableId === source.droppableId) {
        return
      }

      // Перетаскивание Rows
      if (type === 'ROWS') {
        const reorderedRows = reorderRows(rows, source.index, destination.index)
        setValue('rows', reorderedRows, { shouldDirty: true })

        return
      }

      // Перетаскивание Elements
      if (type === 'ELEMENTS') {
        const sourceDroppableId = source.droppableId
        const destDroppableId = destination.droppableId

        // Добавление нового пустого поля
        if (result.draggableId === 'NEW:FIELD') {
          setFieldConfigState({
            mode: 'create',
            destination: { droppableId: destDroppableId, index: destination.index },
            initialData: undefined, // Пустое поле
          })

          return
        }

        // Добавление поля из Available Fields (предзаполнение)
        if (sourceDroppableId.startsWith('FIELDS:')) {
          const commandKey = sourceDroppableId.split(':')[1]
          const fieldsForCommand = availableFields.filter(f => f.commandKey === commandKey)
          const field = fieldsForCommand[source.index]

          if (!field) return

          // Находим команду для предзаполнения
          const command = commandsInfo.find(c => c.commandKey === commandKey)
          const commandForDialog = command
            ? {
                key: command.commandKey,
                name: command.commandName,
                objectCode: command.objectCode,
                objectTitle: command.objectTitle,
              }
            : {
                key: field.commandKey,
                name: field.commandName,
                objectCode: field.objectCode,
                objectTitle: field.objectCode,
              }

          const pathArrayForDialog = [
            {
              objectCode: command?.objectCode || field.objectCode,
              refValueType: FIELD_VALUE_TYPE.OBJ_PK_LINK,
              field: field.name,
              valueType: field.valueType,
              isPk: field.isPk,
              isInternalId: field.isInternalId,
              model: field.model || null,
            },
          ]

          // Открываем диалог с предзаполненными данными
          setFieldConfigState({
            mode: 'create',
            destination: { droppableId: destDroppableId, index: destination.index },
            initialData: {
              title: field.name, // По умолчанию - имя поля
              bindType: 'field',
              command: commandForDialog,
              field,
              useDisplayRule: false,
              displayRule: null,
              pathArray: pathArrayForDialog,
              formatDate: null,
            },
          })

          return
        }

        // Добавление поля из Variables
        if (sourceDroppableId === 'VARIABLES') {
          const variable = variables[source.index]

          if (!variable) return

          // Открываем диалог с предзаполненными данными для переменной
          setFieldConfigState({
            mode: 'create',
            destination: { droppableId: destDroppableId, index: destination.index },
            initialData: {
              title: variable.internalId,
              bindType: 'js',
              jsCode: `return variables.${variable.internalId}`,
              useDisplayRule: false,
              displayRule: null,
              formatDate: null,
            },
          })

          return
        }

        // Перемещение внутри одного Row
        if (sourceDroppableId === destDroppableId) {
          const rowIndex = parseInt(sourceDroppableId.split(':')[1])
          const updatedElements = reorderRowElements(
            rows[rowIndex].elements,
            source.index,
            destination.index
          )

          const updatedRows = [...rows]
          updatedRows[rowIndex] = { ...rows[rowIndex], elements: updatedElements }

          setValue('rows', updatedRows, { shouldDirty: true })

          return
        }

        // Перемещение между Rows
        const sourceRowIndex = parseInt(sourceDroppableId.split(':')[1])
        const destRowIndex = parseInt(destDroppableId.split(':')[1])

        const updatedRows = moveElementBetweenRows(
          rows,
          sourceRowIndex,
          destRowIndex,
          source.index,
          destination.index
        )

        setValue('rows', updatedRows, { shouldDirty: true })
      }
    },
    [rows, availableFields, variables, setValue]
  )

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box sx={{ display: 'flex', height: '600px', gap: 2 }}>
          <AvailableFieldsPanel objectFields={availableFields} variables={variables} />
          <Divider orientation='vertical' flexItem />
          <ConfiguredRowsPanel
            rows={rows}
            onAddRow={handleAddRow}
            onDeleteRow={handleDeleteRow}
            onEditElement={handleEditElement}
            onDeleteElement={handleDeleteElement}
          />
        </Box>
      </DragDropContext>

      {/* Универсальный FieldConfigDialog для создания/редактирования полей */}
      {fieldConfigState && (
        <FieldConfigDialog
          open
          mode={fieldConfigState.mode}
          initialData={fieldConfigState.initialData}
          commands={commandsInfo}
          dataSource={dataSource}
          onClose={() => setFieldConfigState(null)}
          onSave={handleSaveFieldConfig}
        />
      )}
    </>
  )
}
